import React, { useState, useEffect, useRef } from 'react';
import './PinPrompt.css';

function PinPrompt({ isOpen, onClose, onVerify, partnerName, gender }) {
  const [pin, setPin] = useState(['', '', '', '']);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const inputRefs = useRef([]);

  useEffect(() => {
    if (isOpen && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
    // Reset PIN when modal opens
    if (isOpen) {
      setPin(['', '', '', '']);
      setError('');
    }
  }, [isOpen]);

  const handleChange = (index, value) => {
    // Only allow digits
    if (!/^\d*$/.test(value)) return;

    const newPin = [...pin];
    newPin[index] = value;
    setPin(newPin);
    setError('');

    // Auto-focus next field
    if (value && index < 3) {
      inputRefs.current[index + 1].focus();
    }

    // Auto-submit when all 4 digits entered
    if (index === 3 && value) {
      const fullPin = [...newPin.slice(0, 3), value].join('');
      handleVerify(fullPin);
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace to go to previous field
    if (e.key === 'Backspace' && !pin[index] && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 4);
    
    if (/^\d{4}$/.test(pastedData)) {
      const newPin = pastedData.split('');
      setPin(newPin);
      setError('');
      inputRefs.current[3].focus();
      
      // Auto-submit pasted PIN
      setTimeout(() => {
        handleVerify(pastedData);
      }, 100);
    }
  };

  const handleVerify = async (pinValue = pin.join('')) => {
    if (pinValue.length !== 4) {
      setError('Please enter all 4 digits');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const isValid = await onVerify(pinValue, gender);
      
      if (!isValid) {
        setError('Incorrect PIN. Please try again.');
        setPin(['', '', '', '']);
        inputRefs.current[0].focus();
      } else {
        // Success - modal will close from parent component
        setPin(['', '', '', '']);
      }
    } catch (err) {
      setError('Error verifying PIN. Please try again.');
      console.error('PIN verification error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPin = () => {
    onClose();
    // Navigate to reset PIN page
    window.location.href = '/reset-pin';
  };

  if (!isOpen) return null;

  const genderIcon = gender === 'male' ? '♂️' : '♀️';
  const genderLabel = gender === 'male' ? 'Male' : 'Female';

  return (
    <div className="pin-prompt-overlay" onClick={onClose}>
      <div className="pin-prompt-modal" onClick={(e) => e.stopPropagation()}>
        <button className="pin-prompt-close" onClick={onClose} aria-label="Close">
          ×
        </button>
        
        <div className="pin-prompt-header">
          <div className="gender-icon">{genderIcon}</div>
          <h3>Enter {genderLabel} Partner PIN</h3>
          <p>View private entries for <strong>{partnerName}</strong></p>
        </div>

        <div className="pin-input-container" onPaste={handlePaste}>
          {pin.map((digit, index) => (
            <input
              key={index}
              ref={(el) => (inputRefs.current[index] = el)}
              type="password"
              inputMode="numeric"
              maxLength="1"
              value={digit}
              onChange={(e) => handleChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              className="pin-digit-input"
              disabled={loading}
              autoComplete="off"
              aria-label={`PIN digit ${index + 1}`}
            />
          ))}
        </div>

        {error && (
          <div className="pin-error-message" role="alert">
            {error}
          </div>
        )}

        <div className="pin-prompt-actions">
          <button 
            className="pin-verify-button"
            onClick={() => handleVerify()}
            disabled={loading || pin.join('').length !== 4}
          >
            {loading ? (
              <>
                <span className="spinner-small"></span>
                Verifying...
              </>
            ) : (
              'Verify PIN'
            )}
          </button>
          
          <button 
            className="pin-forgot-button"
            onClick={handleForgotPin}
            type="button"
          >
            Forgot PIN?
          </button>
        </div>

        <div className="pin-prompt-footer">
          <p className="security-note">
            🔒 Your PIN is encrypted and secure
          </p>
        </div>
      </div>
    </div>
  );
}

export default PinPrompt;